<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';

import { useMapGetter } from 'dashboard/composables/store';
import { useVisitasStore } from 'dashboard/stores/visitas';
import { frontendURL } from 'dashboard/helper/URLHelper';
import LaisActionBar from 'dashboard/components-next/LaisActionBar.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisInfoRow from 'dashboard/components-next/LaisInfoRow.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SimilarListings from 'dashboard/components-next/SimilarListings.vue';
import RequestVisitFeedbackCard from '../components/RequestVisitFeedbackCard.vue';
import {
  latestPostVisitReportVersion,
  postVisitArtifactFrom,
  postVisitArtifactState,
  postVisitDiffRows,
  postVisitReportVersions,
  postVisitVersionLabel,
} from '../utils/postVisitReport';
import {
  formatVisitDateTime,
  visitFinishTitle,
  shouldShowRequestVisitFeedback,
  visitConfirmationDueDateAlert,
  visitConfirmationStatus,
  visitSuccessTitle,
  visitSummaryRows,
} from '../utils/visitSuccessUtils';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const store = useVisitasStore();
const REQUEST_FEEDBACK_STORAGE_PREFIX = 'lais:visit-request-feedback';

const isLoading = ref(true);
const loadError = ref('');
const visit = ref({});
const requestFeedbackSubmission = ref({
  status: '',
  feedback: '',
});
const context = computed(() => String(route.query.context || 'schedule'));
const confirmation = computed(() => visitConfirmationStatus(visit.value));
const dueAlert = computed(() => visitConfirmationDueDateAlert(visit.value));
const rows = computed(() => visitSummaryRows(visit.value));
const postVisitArtifact = computed(() => postVisitArtifactFrom(visit.value));
const latestReportVersion = computed(() =>
  latestPostVisitReportVersion(postVisitArtifact.value)
);
const reportVersions = computed(() =>
  postVisitReportVersions(postVisitArtifact.value)
);
const latestReportDiffRows = computed(() =>
  postVisitDiffRows(latestReportVersion.value)
);
const postVisitState = computed(() => postVisitArtifactState(visit.value));
const postVisitStateLabel = computed(
  () =>
    ({
      done: 'Resultado realizado',
      not_done: 'Resultado não realizado',
      no_show: 'Não compareceu',
      review: 'Em revisão',
      loading: 'Carregando',
      empty: 'Sem laudo versionado',
    })[postVisitState.value] || 'Em revisão'
);
const showRequestFeedback = computed(() =>
  shouldShowRequestVisitFeedback({
    context: context.value,
    visit: visit.value,
  })
);
const title = computed(() =>
  context.value === 'finish'
    ? visitFinishTitle({ status: visit.value.status, t })
    : visitSuccessTitle({
        context: context.value,
        status: visit.value.status,
        t,
      })
);
const imovelId = computed(
  () => visit.value?.imovel?.id || visit.value?.imovelId
);
const requestFeedbackMetadata = computed(
  () =>
    visit.value?.requestVisitFeedback ||
    visit.value?.request_visit_feedback ||
    {}
);
const hasRequestFeedbackSubmissionContext = computed(
  () =>
    context.value === 'register' ||
    ['sent', 'error', 'sending'].includes(
      String(route.query.feedback_status || '')
    )
);
const requestFeedbackNotice = computed(() => {
  if (!hasRequestFeedbackSubmissionContext.value) return null;

  if (
    requestFeedbackMetadata.value?.status === 'submitted' ||
    requestFeedbackSubmission.value.status === 'sent'
  ) {
    return {
      kind: 'success',
      icon: 'i-lucide-send',
      title: 'Enviaremos ao proprietário!',
      description: 'A mensagem ao proprietário foi registrada com sucesso.',
      tone: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      iconTone: 'text-emerald-700',
      action: false,
    };
  }

  if (requestFeedbackSubmission.value.status === 'sending') {
    return {
      kind: 'loading',
      icon: 'i-lucide-loader-circle animate-spin',
      title: 'Enviando mensagem ao proprietário',
      description: 'Aguarde enquanto registramos o feedback da visita.',
      tone: 'border-lais-border bg-white text-lais-text',
      iconTone: 'text-lais-primary',
      action: false,
    };
  }

  if (requestFeedbackSubmission.value.status === 'error') {
    return {
      kind: 'error',
      icon: 'i-lucide-triangle-alert',
      title: 'Não conseguimos enviar a mensagem ao proprietário',
      description:
        'Reenvie para manter o proprietário informado sobre o que o visitante achou do imóvel.',
      tone: 'border-amber-200 bg-amber-50 text-amber-900',
      iconTone: 'text-amber-700',
      action: true,
    };
  }

  return null;
});

const requestFeedbackStorageKey = visitId =>
  `${REQUEST_FEEDBACK_STORAGE_PREFIX}:${visitId}`;

const readPendingRequestFeedback = visitId => {
  if (!visitId || typeof window === 'undefined') return '';

  try {
    return JSON.parse(
      window.sessionStorage?.getItem(requestFeedbackStorageKey(visitId)) || '{}'
    )?.feedback;
  } catch {
    return '';
  }
};

const clearPendingRequestFeedback = visitId => {
  if (!visitId || typeof window === 'undefined') return;

  window.sessionStorage?.removeItem(requestFeedbackStorageKey(visitId));
};

const syncRequestFeedbackSubmission = () => {
  if (requestFeedbackMetadata.value?.status === 'submitted') {
    requestFeedbackSubmission.value = {
      status: 'sent',
      feedback: '',
    };
    clearPendingRequestFeedback(route.params.id);
    return;
  }

  if (route.query.feedback_status === 'error') {
    requestFeedbackSubmission.value = {
      status: 'error',
      feedback: readPendingRequestFeedback(route.params.id) || '',
    };
    return;
  }

  if (route.query.feedback_status === 'sent') {
    requestFeedbackSubmission.value = {
      status: 'sent',
      feedback: '',
    };
  }
};

const loadVisit = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    visit.value = camelcaseKeys(await store.show(route.params.id), {
      deep: true,
    });
    syncRequestFeedbackSubmission();
  } catch {
    loadError.value =
      'Nao foi possivel carregar o laudo versionado desta visita.';
  } finally {
    isLoading.value = false;
  }
};

const goAgenda = () =>
  router.push(frontendURL(`accounts/${accountId.value}/agenda`));
const newVisit = () =>
  router.push(frontendURL(`accounts/${accountId.value}/visitas/new`));
const goVisitDetails = () =>
  router.push(
    frontendURL(`accounts/${accountId.value}/visitas/${route.params.id}`)
  );
const resendRequestFeedback = async () => {
  const feedback =
    requestFeedbackSubmission.value.feedback ||
    readPendingRequestFeedback(route.params.id);

  if (!feedback) return;

  requestFeedbackSubmission.value = {
    status: 'sending',
    feedback,
  };

  try {
    visit.value = camelcaseKeys(
      await store.submitRequestFeedback(route.params.id, { feedback }),
      { deep: true }
    );
    clearPendingRequestFeedback(route.params.id);
    requestFeedbackSubmission.value = {
      status: 'sent',
      feedback: '',
    };
  } catch {
    requestFeedbackSubmission.value = {
      status: 'error',
      feedback,
    };
  }
};

onMounted(loadVisit);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg-page font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      title="Visita"
      helper="Resumo da operação registrada na agenda."
      :breadcrumb="[
        { label: 'Visitas', to: { name: 'visitas_index' } },
        { label: 'Sucesso' },
      ]"
    />

    <div
      class="min-h-0 flex-1 overflow-auto px-4 pb-24 pt-8 sm:px-6 sm:pb-8 lg:px-10 max-md:flex-none max-md:overflow-visible"
    >
      <div
        v-if="isLoading"
        class="py-16 text-center text-[13px] text-lais-text-muted"
      >
        {{ t('VISITS.COMMON.LOADING') }}
      </div>

      <div
        v-else-if="loadError"
        class="mx-auto grid max-w-xl gap-4 py-16 text-center"
        data-test="visita-success-error"
        role="alert"
      >
        <span
          class="i-lucide-alert-circle mx-auto size-10 text-rose-600"
          aria-hidden="true"
        />
        <p class="text-[14px] leading-relaxed text-lais-text-muted">
          {{ loadError }}
        </p>
      </div>

      <div
        v-else
        class="mx-auto grid max-w-3xl gap-5"
        data-test="visita-success-page"
      >
        <RequestVisitFeedbackCard
          v-if="showRequestFeedback"
          :visit="visit"
          @details="goVisitDetails"
        />

        <LaisCard v-if="!showRequestFeedback">
          <div class="grid gap-4 text-center">
            <span
              class="mx-auto grid size-14 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
            >
              <span class="i-lucide-check size-7" aria-hidden="true" />
            </span>
            <div>
              <h1
                class="font-redHatDisplay text-[28px] font-semibold text-lais-text"
              >
                {{ title }}
              </h1>
              <p class="mt-2 text-[14px] text-lais-text-muted">
                A visita ficou salva na agenda da imobiliária.
              </p>
            </div>
            <span
              class="mx-auto inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-[12px] font-semibold"
              :class="confirmation.tone"
            >
              <span
                :class="confirmation.icon"
                class="size-4"
                aria-hidden="true"
              />
              {{ confirmation.label }}
            </span>
          </div>
        </LaisCard>

        <div
          v-if="requestFeedbackNotice"
          class="flex flex-col gap-3 rounded-lg border px-4 py-3 text-left sm:flex-row sm:items-center sm:justify-between"
          :class="requestFeedbackNotice.tone"
          role="status"
        >
          <div class="flex min-w-0 gap-3">
            <span
              :class="[
                requestFeedbackNotice.icon,
                requestFeedbackNotice.iconTone,
              ]"
              class="mt-0.5 size-5 shrink-0"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <p class="text-[13px] font-semibold">
                {{ requestFeedbackNotice.title }}
              </p>
              <p class="mt-1 text-[13px] leading-5">
                {{ requestFeedbackNotice.description }}
              </p>
            </div>
          </div>
          <LaisPrimaryButton
            v-if="requestFeedbackNotice.action"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            class="justify-center sm:w-auto"
            @click="resendRequestFeedback"
          >
            Reenviar feedback
          </LaisPrimaryButton>
        </div>

        <div
          v-if="!showRequestFeedback && dueAlert.visible"
          class="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-amber-900"
          role="status"
        >
          <span
            :class="dueAlert.icon"
            class="mt-0.5 size-5 shrink-0 text-amber-600"
            aria-hidden="true"
          />
          <div class="min-w-0">
            <p class="text-[13px] font-semibold">
              {{ dueAlert.title }}
            </p>
            <p class="mt-1 text-[13px] leading-5 text-amber-800">
              {{ dueAlert.label }}
            </p>
          </div>
        </div>

        <LaisCard v-if="!showRequestFeedback" title="Resumo">
          <div class="grid gap-3">
            <LaisInfoRow
              v-for="row in rows"
              :key="row.label"
              :icon="row.icon"
              :label="row.label"
              :value="row.value"
            />
          </div>
        </LaisCard>

        <LaisCard
          v-if="!showRequestFeedback && latestReportVersion"
          title="Laudo pós-visita"
          data-test="visita-success-report-version"
        >
          <div class="grid gap-4">
            <div
              class="flex flex-col gap-3 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-body sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p class="font-semibold text-lais-text">
                  {{ postVisitVersionLabel(latestReportVersion) }}
                </p>
                <p class="mt-1 text-lais-text-muted">
                  {{ postVisitStateLabel }} -
                  {{
                    formatVisitDateTime(
                      latestReportVersion.submittedAt ||
                        latestReportVersion.submitted_at
                    )
                  }}
                </p>
              </div>
              <span
                class="inline-flex w-fit rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-lais-primary"
              >
                {{ reportVersions.length }} versões
              </span>
            </div>

            <div v-if="latestReportDiffRows.length" class="grid gap-2">
              <div
                v-for="row in latestReportDiffRows"
                :key="row.path"
                class="grid gap-1 rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] text-lais-text-body"
              >
                <p class="font-semibold text-lais-text">{{ row.path }}</p>
                <p class="text-lais-text-muted">
                  Antes: {{ row.before }} - Depois: {{ row.after }}
                </p>
              </div>
            </div>
            <p v-else class="text-[13px] leading-relaxed text-lais-text-muted">
              Primeira versão salva. Revisões futuras aparecerão aqui com diff
              redigido.
            </p>
          </div>
        </LaisCard>

        <SimilarListings
          v-if="imovelId"
          :imovel-id="imovelId"
          :account-id="accountId"
          title="Talvez o cliente também goste"
        />
      </div>
    </div>

    <LaisActionBar sticky-mobile wrap-mobile>
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-calendar-days"
          @click="goAgenda"
        >
          Ver agenda
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-list-checks"
          @click="goVisitDetails"
        >
          Ver detalhe
        </LaisPrimaryButton>
        <LaisPrimaryButton icon="i-lucide-plus" @click="newVisit">
          Nova visita
        </LaisPrimaryButton>
      </template>
    </LaisActionBar>
  </section>
</template>
